import { ActionIcon, Button, Divider, Menu, NumberInput, Switch, Tooltip } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const createStrictNumberChangeHandler =
    (min: number, max: number, setValue: (nextValue: number) => void, fallbackValue: number) =>
    (value: string | number) => {
        if (value === '' || typeof value !== 'number' || Number.isNaN(value)) {
            setValue(clamp(fallbackValue, min, max));
        } else {
            setValue(clamp(value, min, max));
        }
    };

const numberInputProps = {
    clampBehavior: 'strict' as const
};

const CameraSettingHint = ({ min, max }: { min: string; max: string }) => (
    <span className="text-icon-xs font-normal normal-case tracking-normal text-text-ultra-light">{`(${min} to ${max})`}</span>
);

type GroupOption<T extends string> = {
    label: string;
    value: T;
};

const CameraSettingGroup = <T extends string>({
    value,
    options,
    onSelect
}: {
    value: T;
    options: GroupOption<T>[];
    onSelect: (next: T) => void;
}) => (
    <Button.Group className="flex flex-wrap">
        {options.map((option) => (
            <Button
                key={option.value}
                className="text-sm min-w-0 flex-1 data-[selected=true]:bg-background-action-hover data-[selected=false]:bg-background-default justify-center"
                data-selected={value === option.value}
                size="xs"
                tabIndex={-1}
                variant={variants.button.secondary}
                onClick={() => onSelect(option.value)}
            >
                {option.label}
            </Button>
        ))}
    </Button.Group>
);

export const CameraSettings = () => {
    const { t } = useTranslation();

    const {
        mainCameraProjectionType,
        setMainCameraProjectionType,
        mainCameraSettings,
        setMainCameraSettings,
        resetMainCameraSettings
    } = usePlacementStore(
        useShallow((state) => ({
            mainCameraProjectionType: state.mainCameraProjectionType,
            setMainCameraProjectionType: state.setMainCameraProjectionType,
            mainCameraSettings: state.mainCameraSettings,
            setMainCameraSettings: state.setMainCameraSettings,
            resetMainCameraSettings: state.resetMainCameraSettings
        }))
    );

    const projectionOptions: GroupOption<CameraProjectionType>[] = [
        { label: t('labelCameraProjectionOrthographic'), value: CameraProjectionType.ORTHOGRAPHIC },
        { label: t('labelCameraProjectionPerspective'), value: CameraProjectionType.PERSPECTIVE }
    ];

    return (
        <Menu closeOnItemClick={false} position="left-start" shadow="md">
            <Menu.Target>
                <div className="absolute bottom-25 left-1/2 transform -translate-x-1/2">
                    <Tooltip label={t('labelCameraOptions')} position="left" variant={variants.tooltip.left}>
                        <ActionIcon className="bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto action-icon-medium icon-lg!">
                            <Icon className="font-light" icon="settings_video_camera" size="xl" />
                        </ActionIcon>
                    </Tooltip>
                </div>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>{t('labelCameraOptions')}</Menu.Label>
                <Menu.Divider />
                <div className="flex max-h-[75vh] w-fit min-w-0 max-w-[calc(100vw-3rem)] flex-col gap-2 overflow-y-auto overscroll-contain px-2 py-2">
                    <div className="text-xs font-medium uppercase tracking-wide text-text-light">
                        {t('labelCameraProjection')}
                    </div>
                    <div className="pl-1">
                        <CameraSettingGroup
                            options={projectionOptions}
                            value={mainCameraProjectionType}
                            onSelect={setMainCameraProjectionType}
                        />
                    </div>

                    <div className="mt-2 text-xs font-medium uppercase tracking-wide text-text-light">
                        {t('labelCameraDynamics')}
                    </div>
                    <div className="pl-1">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-sm">{t('labelCameraDollySpeed')}</span>
                                    <CameraSettingHint max="8" min="0.1" />
                                </div>
                                <NumberInput
                                    className="rounded-sm border border-border-light"
                                    decimalScale={2}
                                    {...numberInputProps}
                                    max={8}
                                    min={0.1}
                                    step={0.1}
                                    styles={{ input: { width: 80, height: '1.75rem' } }}
                                    value={mainCameraSettings.dollySpeed}
                                    onChange={createStrictNumberChangeHandler(
                                        0.1,
                                        8,
                                        (dollySpeed) => setMainCameraSettings({ dollySpeed }),
                                        mainCameraSettings.dollySpeed
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-sm">{t('labelCameraTruckSpeed')}</span>
                                    <CameraSettingHint max="8" min="0.1" />
                                </div>
                                <NumberInput
                                    className="rounded-sm border border-border-light"
                                    decimalScale={2}
                                    {...numberInputProps}
                                    max={8}
                                    min={0.1}
                                    step={0.1}
                                    styles={{ input: { width: 80, height: '1.75rem' } }}
                                    value={mainCameraSettings.truckSpeed}
                                    onChange={createStrictNumberChangeHandler(
                                        0.1,
                                        8,
                                        (truckSpeed) => setMainCameraSettings({ truckSpeed }),
                                        mainCameraSettings.truckSpeed
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-sm">{t('labelCameraDraggingSmoothTime')}</span>
                                    <CameraSettingHint max="0.25" min="0" />
                                </div>
                                <NumberInput
                                    className="rounded-sm border border-border-light"
                                    decimalScale={3}
                                    {...numberInputProps}
                                    max={0.25}
                                    min={0}
                                    step={0.01}
                                    styles={{ input: { width: 80, height: '1.75rem' } }}
                                    value={mainCameraSettings.draggingSmoothTime}
                                    onChange={createStrictNumberChangeHandler(
                                        0,
                                        0.25,
                                        (draggingSmoothTime) => setMainCameraSettings({ draggingSmoothTime }),
                                        mainCameraSettings.draggingSmoothTime
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-sm">{t('labelCameraSmoothTime')}</span>
                                    <CameraSettingHint max="1" min="0" />
                                </div>
                                <NumberInput
                                    className="rounded-sm border border-border-light"
                                    decimalScale={3}
                                    {...numberInputProps}
                                    max={1}
                                    min={0}
                                    step={0.01}
                                    styles={{ input: { width: 80, height: '1.75rem' } }}
                                    value={mainCameraSettings.smoothTime}
                                    onChange={createStrictNumberChangeHandler(
                                        0,
                                        1,
                                        (smoothTime) => setMainCameraSettings({ smoothTime }),
                                        mainCameraSettings.smoothTime
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-sm">{t('labelCameraAzimuthRotateSpeed')}</span>
                                    <CameraSettingHint max="4" min="-4" />
                                </div>
                                <NumberInput
                                    className="rounded-sm border border-border-light"
                                    decimalScale={2}
                                    {...numberInputProps}
                                    max={4}
                                    min={-4}
                                    step={0.1}
                                    styles={{ input: { width: 80, height: '1.75rem' } }}
                                    value={mainCameraSettings.azimuthRotateSpeed}
                                    onChange={createStrictNumberChangeHandler(
                                        -4,
                                        4,
                                        (azimuthRotateSpeed) => setMainCameraSettings({ azimuthRotateSpeed }),
                                        mainCameraSettings.azimuthRotateSpeed
                                    )}
                                />
                            </div>

                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-sm">{t('labelCameraPolarRotateSpeed')}</span>
                                    <CameraSettingHint max="4" min="-4" />
                                </div>
                                <NumberInput
                                    className="rounded-sm border border-border-light"
                                    decimalScale={2}
                                    {...numberInputProps}
                                    max={4}
                                    min={-4}
                                    step={0.1}
                                    styles={{ input: { width: 80, height: '1.75rem' } }}
                                    value={mainCameraSettings.polarRotateSpeed}
                                    onChange={createStrictNumberChangeHandler(
                                        -4,
                                        4,
                                        (polarRotateSpeed) => setMainCameraSettings({ polarRotateSpeed }),
                                        mainCameraSettings.polarRotateSpeed
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-2 text-xs font-medium uppercase tracking-wide text-text-light">
                        {t('labelCameraBehavior')}
                    </div>
                    <div className="pl-1">
                        <div className="flex flex-col gap-1">
                            <Switch
                                checked={mainCameraSettings.dollyToCursor}
                                label={t('labelCameraDollyToCursor')}
                                size="xs"
                                onChange={(event) =>
                                    setMainCameraSettings({ dollyToCursor: event.currentTarget.checked })
                                }
                            />
                        </div>
                    </div>
                </div>
                <Divider />
                <div className="flex items-center justify-center p-2">
                    <Button
                        className="w-full"
                        leftSection={<Icon className="mr-3" icon="refresh" />}
                        size={sizes.button.small}
                        variant={variants.button.secondary}
                        onPointerDown={resetMainCameraSettings}
                    >
                        {t('labelCameraResetSettings')}
                    </Button>
                </div>
            </Menu.Dropdown>
        </Menu>
    );
};
